import { ChangeEvent, useMemo, useState } from "react";

import { useCreateOrderReport, useCurrentWorkspace } from "@web-app/api";
import {
  Button,
  Header,
  Input,
  Skeleton,
  TextArea,
  useAlert,
} from "@web-app/components";
import { Order, OrderDeliveryType } from "@web-app/types";

interface OrderReportIssuePanelProps {
  onClose: () => void;
  order: Order;
}

const fallbackValue = "—";
const otherReasonTopic = "__OTHER_REASON__";

function displayText(value: string | null | undefined) {
  return value?.trim() || fallbackValue;
}

function formatValue(value: number | null | undefined, currency?: string) {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return fallbackValue;
  }

  try {
    return new Intl.NumberFormat(undefined, {
      currency: currency || "USD",
      style: "currency",
    }).format(value);
  } catch {
    return new Intl.NumberFormat(undefined, {
      currency: "USD",
      style: "currency",
    }).format(value);
  }
}

export default function OrderReportIssuePanel({
  onClose,
  order,
}: OrderReportIssuePanelProps) {
  const [description, setDescription] = useState("");
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
  const { mutateAsync: createOrderReport, isPending } = useCreateOrderReport();
  const { showAlertAsync } = useAlert();
  const { data: currentWorkspace, isLoading: isWorkspaceLoading } =
    useCurrentWorkspace();
  const isPickupOrder = order.type === OrderDeliveryType.ROUTE_OPT_PICKUP;
  const location = isPickupOrder ? order.pickup : order.dropoff;
  const details = order.details;
  const isOtherReasonSelected = selectedTopic === otherReasonTopic;
  const isSubmitDisabled =
    !selectedTopic ||
    isPending ||
    (isOtherReasonSelected && !description.trim());
  const reportOptions = useMemo(() => {
    const options =
      currentWorkspace?.settings?.reportOptions?.dispatchPage ?? [];

    return Array.isArray(options)
      ? options.filter((option) => option.message.trim().length > 0)
      : [];
  }, [currentWorkspace?.settings?.reportOptions?.dispatchPage]);

  const handleTopicChange = (topic: string) => {
    setSelectedTopic((currentTopic) => (currentTopic === topic ? null : topic));
  };

  const handleSubmit = async () => {
    if (!selectedTopic || (isOtherReasonSelected && !description.trim())) {
      return null;
    }

    const topic = selectedTopic.trim();
    const payload = isOtherReasonSelected
      ? {
          description: description.trim(),
          type: "OTHER_REASON",
        }
      : {
          description: description.trim() || topic,
          type: topic.toUpperCase().replace(/\s+/g, "_"),
        };

    try {
      await showAlertAsync(createOrderReport({ orderId: order.id, payload }), {
        error: {
          message:
            "Failed to submit report. Please try again or contact support",
          title: "Error",
          type: "error",
        },
        loading: {
          message: "Please wait...",
          title: "Submitting Report",
          type: "default",
        },
        success: {
          message: "Successfully submitted report",
          title: "Success",
          type: "success",
        },
      });
      onClose();
    } catch {
      // The alert displays the mutation error; keep the panel open for retry.
    }
  };

  return (
    <div className="absolute z-30 flex h-[100dvh] w-full flex-col overflow-hidden bg-white lg:h-full">
      <Header
        title={displayText(order.displayId)}
        borderLess
        leftActions={
          <Button
            icon="fasChevronLeft"
            onClick={onClose}
            plain
            className="!p-0 hover:!bg-white"
            iconClassName="!h-3 !w-3 lg:!h-4 lg:!w-4"
          />
        }
      />
      <div className="flex-1 overflow-x-hidden overflow-y-auto bg-white px-4 pb-6">
        <div className="flex flex-col gap-5 lg:gap-6">
          <section>
            <h3 className="mb-1 text-sm font-semibold text-black">
              Order Details
            </h3>
            <div className="flex min-w-0 flex-col gap-[6px] rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-black">
              <p className="break-words">
                Customer: {displayText(location?.name)}
              </p>
              <p className="break-words">
                Destination: {displayText(location?.address)}
              </p>
              <p>Item Count: {details?.itemsCount ?? fallbackValue}</p>
              <p>Value: {formatValue(details?.value, details?.currency)}</p>
            </div>
          </section>

          <section>
            <h3 className="mb-2 text-sm font-semibold text-black">
              Select issue topic(s): <span className="text-red-500">*</span>
            </h3>
            <div className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
              {isWorkspaceLoading
                ? Array.from({ length: 4 }, (_, index) => (
                    <div key={index} className="flex items-center gap-2 py-1">
                      <Skeleton height="h-3.5" width="w-3.5" />
                      <Skeleton height="h-3.5" width="w-28" />
                    </div>
                  ))
                : reportOptions.map((topic) => (
                    <Input
                      key={topic.message}
                      labelClassName="min-w-0 !text-sm !text-black"
                      rightLabel={topic.message}
                      type="checkbox"
                      value={selectedTopic === topic.message ? "true" : "false"}
                      wrapText
                      onChange={() => handleTopicChange(topic.message)}
                    />
                  ))}
              <Input
                labelClassName="min-w-0 !text-sm !text-black"
                rightLabel="Other"
                type="checkbox"
                value={isOtherReasonSelected ? "true" : "false"}
                wrapText
                onChange={() => handleTopicChange(otherReasonTopic)}
              />
            </div>
          </section>

          <section>
            <h3 className="mb-2 text-sm font-semibold text-black">
              Describe the issue in detail
            </h3>
            <TextArea
              className="min-h-[100px] !text-sm !text-black focus:!border-gray-300 focus:!ring-gray-100"
              controlled
              isFullWidth
              placeholder="Describe your issue here"
              rows={3}
              value={description}
              onChange={(value: ChangeEvent<HTMLTextAreaElement> | string) =>
                setDescription(
                  typeof value === "string" ? value : value.target.value,
                )
              }
            />
          </section>
        </div>
      </div>
      <div className="flex shrink-0 justify-end gap-2 border-t border-gray-200 bg-white px-4 py-2 sm:px-6 md:py-3 lg:py-5">
        <Button color="light" onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={isSubmitDisabled} onClick={handleSubmit}>
          Submit Report
        </Button>
      </div>
    </div>
  );
}
